import React from 'react'
import { margin, MarginProps } from 'styled-system'

import { UnknownRecord } from 'types/global'
import styled, { CSSProperties, keyframes } from 'styled-components'
import { colors } from 'utils/colors'

type Props = MarginProps & {
  borderRadius?: number
  color?: keyof typeof colors
  count?: number
  duration?: number
  width?: string
  wrapper?: React.JSXElementConstructor<UnknownRecord>
  height?: string
  circle?: boolean
  style?: UnknownRecord
  className?: string
  row?: boolean
}
const defaultBaseColor: keyof typeof colors = 'gray2'

const defaultHighlightColor = colors.gray9

export const skeletonKeyframes = keyframes`
  0% {
    background-position: -200px 0;
  }

  100% {
    background-position: calc(200px + 100%) 0;
  }
`

const StyledDiv = styled.div<{ borderRadius: number; color: keyof typeof colors; duration: number }>`
  background-color: ${(p) => p.theme.colors[p.color]};
  background-image: linear-gradient(
    90deg,
    ${(p) => p.theme.colors[p.color]},
    ${defaultHighlightColor},
    ${(p) => p.theme.colors[p.color]}
  );
  background-size: 200px 100%;
  background-repeat: no-repeat;
  border-radius: ${(p) => p.borderRadius}px;
  display: inline-block;
  line-height: 1;
  width: 100%;
  animation: ${skeletonKeyframes} ${(p) => p.duration}s ease-in-out infinite;
`

const SkeletonWrapper = styled.div<MarginProps & { width?: string } & { row: boolean }>`
  ${margin}
  display: flex;
  flex-direction: ${(p) => (p.row ? `column` : `row`)};
  gap: 10px;
  align-items: ${(p) => (p.row ? `start` : `center`)};
  ${(p) => (p.width ? `width: ${p.width};` : '')}
`

const SkeletonLoader = ({
  borderRadius = 4,
  color = defaultBaseColor as keyof typeof colors,
  count = 1,
  duration = 1.4,
  width = '100%',
  wrapper,
  height = '23px',
  circle,
  style,
  className,
  row = false,
  ...marginProps
}: Props) => {
  const elements = Array(count).fill('')
  const customStyle = style || {}
  const customClassName = className
  const Wrapper = wrapper

  const constructedStyles: CSSProperties = {}
  if (width !== null) {
    constructedStyles.width = width
  }

  if (height !== null) {
    constructedStyles.height = height
  }
  if (width !== null && height !== null && circle) {
    constructedStyles.borderRadius = '50%'
  }

  let componentClass = 'react-loading-skeleton'
  if (customClassName) {
    componentClass += ' ' + customClassName
  }

  return (
    <SkeletonWrapper data-testid="skeleton-loader" row={row} {...marginProps} width={width}>
      {Wrapper
        ? elements.map((_, i) => (
            <Wrapper key={i}>
              <StyledDiv
                borderRadius={borderRadius}
                color={color}
                key={i}
                duration={duration}
                className={componentClass}
                style={{ ...constructedStyles, ...customStyle }}
              />
            </Wrapper>
          ))
        : elements.map((_, i) => (
            <StyledDiv
              borderRadius={borderRadius}
              color={color}
              key={i}
              duration={duration}
              className={componentClass}
              style={{ ...constructedStyles, ...customStyle }}
            />
          ))}
    </SkeletonWrapper>
  )
}

export default SkeletonLoader
